// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 MindTenet LLC
// This file is part of Undef Engine.

/**
 * GameBoardRouter - Routes to appropriate game board based on game_id
 *
 * This component detects the current game and renders the appropriate
 * game-specific board component. Supports lazy loading for better performance.
 * Each game board is wrapped in an error boundary for fault isolation.
 */

import { Suspense, type ReactNode, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { GameBoard } from './GameBoard'; // Classic "terrible_psychiatrist" board
import { GameErrorBoundary } from './GameErrorBoundary';
import { hasGameBoard, getGameBoard } from './games/gameBoardRegistry';
import { LoadingWithPersonality } from './shared/LoadingWithPersonality';
import { logger } from '../lib/logger';
import { useGameStore } from '../store/gameStore';
import type { GameState } from '../types/game';

// Loading fallback component with personality
function LoadingBoard() {
  return (
    <div className="flex items-center justify-center min-h-screen">
      <LoadingWithPersonality context="game" />
    </div>
  );
}

// Helper component that wraps game boards with both Suspense and ErrorBoundary
interface GameBoardWrapperProps {
  children: ReactNode;
  gameId: string;
  sessionId: string;
}

function GameBoardWrapper({ children, gameId, sessionId }: GameBoardWrapperProps) {
  return (
    <GameErrorBoundary gameId={gameId} sessionId={sessionId}>
      <Suspense fallback={<LoadingBoard />}>{children}</Suspense>
    </GameErrorBoundary>
  );
}

export function GameBoardRouter() {
  const { sessionId, roomCode, playerId, gameState, setSessionError } = useGameStore();
  const navigate = useNavigate();
  const state = gameState as GameState | null;

  // Track if we've successfully loaded state before (using ref to avoid setState in effect)
  const hasLoadedStateRef = useRef(false);

  // Detect game (computed at top for hooks to use)
  const gameType = state?.game_id || 'terrible-psychiatrist';

  // Dynamic game board lookup via registry (memoized to avoid recreating component)
  // The registry caches lazy components, so this pattern is safe
  const GameBoardComponent = useMemo(() => {
    if (!hasGameBoard(gameType)) {
      logger.warn('game_not_enabled', { gameType, fallback: 'classic board' });
      return null;
    }
    return getGameBoard(gameType);
  }, [gameType]);

  // If we had state but it disappeared, the session likely became invalid
  useEffect(() => {
    if (state) {
      hasLoadedStateRef.current = true;
    } else if (hasLoadedStateRef.current && sessionId && playerId) {
      // We had state before but it's gone - session became invalid
      logger.warn('game_session_became_invalid', { sessionId, playerId });
      setSessionError('Session became invalid. The game may have ended or you were disconnected.');
    }
  }, [state, sessionId, playerId, setSessionError]);

  if (!sessionId || !playerId || !roomCode) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="glass rounded-lg p-8 text-center">
          <p className="text-xl text-red-400">Session error: Missing session data</p>
          <button onClick={() => navigate('/')} className="btn btn-primary mt-4">
            Return Home
          </button>
        </div>
      </div>
    );
  }

  if (!state) {
    return <LoadingBoard />;
  }

  // Fallback to classic board if game not found
  if (!GameBoardComponent) {
    return (
      <GameErrorBoundary gameId={gameType} sessionId={sessionId}>
        <GameBoard />
      </GameErrorBoundary>
    );
  }

  // Registry caches lazy components, so this pattern is safe
  // key={sessionId} forces unmount/remount when session changes, resetting all local state
  /* eslint-disable react-hooks/static-components */
  return (
    <GameBoardWrapper key={sessionId} gameId={gameType} sessionId={sessionId}>
      <GameBoardComponent sessionId={sessionId} playerId={playerId} roomCode={roomCode} />
    </GameBoardWrapper>
  );
  /* eslint-enable react-hooks/static-components */
}
